import {createApi} from '@reduxjs/toolkit/query/react';

import {EChangelogStagesLookupCode} from 'components/@common/widgets/tabbed-form/common-tabs/changelog/changelog.constants';
import {TLookupValue} from 'modules/data/data-types';
import {IAttachment} from 'shared/types/files';

import {axiosBaseQuery} from '../base-query';
import {transformCallTCServiceDataRequest} from './utils';

export interface ITransportContainerDocument {
    id: string;
    docName: string;
    fileList: IAttachment[];
}

export interface ITransportContainerAudit {
    id: string;
    stage: TLookupValue;
    createdDate: string;
    updatedDate: string;
    statusCode: number;
    statusText: string;
}

export interface ITransportContainerDescription {
    id: string;
    name: string;
    numRequirements: string;
    tcDocument: ITransportContainerDocument | null;
    mainDocument: ITransportContainerDocument | null;
    processingResultDocument: ITransportContainerDocument | null;
    responseRequirementDocument: ITransportContainerDocument | null;
    creatingAudit: ITransportContainerAudit;
    downloadingAudit: ITransportContainerAudit | null;
    processingAudit: ITransportContainerAudit | null;
    responseRequirementAudit: ITransportContainerAudit | null;
    audits: ITransportContainerAudit[];
    protocolFormed: boolean;
    dateReceivedProtocol: Date | undefined;
    dateReceivedResponse: Date | undefined;
}

export interface IGetTransportContainerRequest {
    id: string;
}

export interface IGetContainerByDocIdRequest {
    mainDocId: string | null;
}

export interface ICreateTransportContainerRequest {
    containerId: string;
    stage: EChangelogStagesLookupCode;
    controlSupervisoryActivities?: number;
    errorDocument?: File;
    errorDocumentPath: string;
    mainDocument?: File;
    mainDocumentPath: string;
    externalServiceId?: number;
    files?: { file: File }[];
    signs?: { file: File }[];
    periodTM?: number;
    powerOfAttorney?: File;
}

export const TransportContainerApi = createApi({
    reducerPath: 'transportContainerApi',
    baseQuery: axiosBaseQuery(),
    tagTypes: ['TransportContainer'],
    endpoints: builder => ({
        getTransportContainer: builder.query<ITransportContainerDescription, IGetTransportContainerRequest>({
            query: ({id}) => ({
                url: `integration.module/file.sharing/${id}`,
            }),
            providesTags: ['TransportContainer'],
        }),
        callServiceForTransportContainer: builder.mutation<any, ICreateTransportContainerRequest>({
            query: ({
                stage,
                containerId,
                ...tc
            }) => ({
                url: 'integration.module/file.sharing/addStage',
                method: 'POST',
                params: {
                    stage,
                    containerId,
                },
                data: transformCallTCServiceDataRequest(tc),
            }),
            invalidatesTags: ['TransportContainer'],
        }),
        getContainerByDocId: builder.query<ITransportContainerDescription, IGetContainerByDocIdRequest>({
            query: ({mainDocId}) => ({
                url: 'integration.module/file.sharing/getByMainDocId',
                params: {
                    mainDocId,
                },
            }),
        }),
    }),
});

export const {
    useGetContainerByDocIdQuery,
    useGetTransportContainerQuery,
    useCallServiceForTransportContainerMutation,
} = TransportContainerApi;
